import { type FC } from "react";
import { addQueryStrings } from "@userFrontend/utils/addQueryStrings";
import { SalonTourButton } from "@userFrontend/components/Buttons/SalonTourButton";
import { useSalonTourButton } from "@userFrontend/hooks/useSalonTourButton";
import { ExtendScoutDeadlineButton } from "@userFrontend/components/Buttons/ExtendScoutDeadlineButton";
import { useExtendScoutDeadlineButton } from "@userFrontend/hooks/useExtendScoutDeadlineButton";
import { Link } from "@root/shared/components/Link";
import { InquiryMessageLabel } from "@legacy_user_frontend/components/projects/button_contents/src/inquiry_message_label";
import { PopOver } from "@root/shared/components/PopOver";
import { useDetectWindowSizeInfo } from "@userFrontend/hooks/useDetectWindowSize";
import {
  scoutContents,
  scoutContentsItem,
  primaryButtonWrapper,
  scoutPopOverOuter,
  scoutPopOverSubText,
  scoutPopOverText,
  salonTourButtonWrapper,
  deadLineText,
  deadLine,
  tertiaryButton,
  primaryButtonWithPopOverWrapper,
} from "./src/styles.css";

export type ScoutButtonProps = {
  scoutButtonContent: {
    scoutDeadline?: number;
    isScouted: boolean;
    scoutedEntryUrl?: string;
    scoutClientShopId?: number;
    isScoutExtend: boolean;
    extendScoutUrl?: string;
    declineScoutUrl?: string;
  };
  salonTourButtonContent?: {
    isSalonTour: boolean;
    salonTourEntryUrl: string;
  };
  queryStr?: string;
  scoutGaLabel: string;
  hasEntried: boolean;
  workCode: string;
  position: string;
  isFixedButton: boolean;
  pageType: string;
  isApp: boolean;
  isPreview?: boolean;
  dataStyleColumn: number;
  isShowInquiryMessageLabel?: boolean;
};

export const ScoutButton: FC<ScoutButtonProps> = ({
  scoutButtonContent: {
    isScouted,
    scoutDeadline,
    scoutedEntryUrl,
    scoutClientShopId,
    isScoutExtend,
    extendScoutUrl,
    declineScoutUrl,
  },
  salonTourButtonContent,
  queryStr,
  scoutGaLabel,
  hasEntried,
  workCode,
  position,
  isFixedButton,
  pageType,
  isApp,
  isPreview,
  dataStyleColumn,
  isShowInquiryMessageLabel,
}) => {
  const scoutGaAction = `${pageType}_scout_entry_tour-${position}`;
  const { handleClickSalonTourButton } = useSalonTourButton();
  const { handleClickExtendScoutDeadlineButton } = useExtendScoutDeadlineButton();
  const salonTourEntryUrl = !isPreview
    ? addQueryStrings(
        salonTourButtonContent ? salonTourButtonContent.salonTourEntryUrl : "",
        queryStr || "",
      )
    : undefined;
  const { width } = useDetectWindowSizeInfo();

  return (
    <>
      {!isFixedButton && (
        <p className={deadLineText}>
          スカウトの受付期限まであと<span className={deadLine}>{scoutDeadline}</span>日
        </p>
      )}
      <div
        className={scoutContents({
          addMargin: !isFixedButton && !isApp && pageType !== "detail",
        })}
        data-style-column={dataStyleColumn}
      >
        {!hasEntried && (
          <>
            {position === "middle" ? (
              <div className={primaryButtonWithPopOverWrapper}>
                <div className={scoutPopOverOuter}>
                  <PopOver arrowPosition={width > 767 ? "bottomRight" : "bottomCenter"}>
                    <p className={scoutPopOverSubText}>
                      書いていないこと・聞きたいことがあったら...
                    </p>
                    <p className={scoutPopOverText}>質問メッセージも送れます</p>
                  </PopOver>
                </div>
                <Link
                  href={scoutedEntryUrl}
                  theme="primary"
                  className="js-webhook disable-on-preview"
                  data-ga-action={`${pageType}_scout_entry-${position}`}
                  data-ga-label={scoutGaLabel}
                  data-code={workCode}
                  data-client-shop-id={scoutClientShopId}
                  data-scout-view-type="screen_view"
                >
                  スカウトを受ける
                </Link>
              </div>
            ) : (
              <div className={primaryButtonWrapper}>
                <Link
                  href={scoutedEntryUrl}
                  theme="primary"
                  className="js-webhook disable-on-preview"
                  data-ga-action={`${pageType}_scout_entry-${position}`}
                  data-ga-label={scoutGaLabel}
                  data-code={workCode}
                  data-client-shop-id={scoutClientShopId}
                  data-scout-view-type="screen_view"
                >
                  {isShowInquiryMessageLabel && <InquiryMessageLabel />}
                  スカウトを受ける
                </Link>
              </div>
            )}
            {salonTourButtonContent?.isSalonTour && (
              <div className={salonTourButtonWrapper}>
                <SalonTourButton
                  text="見学を希望する"
                  buttonTheme="secondary"
                  gaAction={scoutGaAction}
                  gaLabel={scoutGaLabel}
                  onClick={
                    !isPreview
                      ? () =>
                          handleClickSalonTourButton({
                            isApp,
                            workCode,
                            isScouted,
                            clientShopId: scoutClientShopId,
                            salonTourEntryUrl,
                          })
                      : undefined
                  }
                />
              </div>
            )}
          </>
        )}
        {!isFixedButton && !isApp && (
          <>
            <div className={scoutContentsItem}>
              {isScoutExtend && extendScoutUrl ? (
                <ExtendScoutDeadlineButton
                  gaAction={`${pageType}_scout_extend-${position}`}
                  gaLabel={scoutGaLabel}
                  onClick={() => handleClickExtendScoutDeadlineButton(extendScoutUrl || "")}
                >
                  スカウト期限
                  <br />
                  延長する
                </ExtendScoutDeadlineButton>
              ) : (
                <button type="button" className={tertiaryButton} disabled>
                  <span>スカウト期限</span>
                  <span>延長済み</span>
                </button>
              )}
            </div>
            <div className={scoutContentsItem}>
              <a
                href={declineScoutUrl}
                className={`${tertiaryButton} js-scout-decline`}
                data-ga-action={`${pageType}_scout_decline-${position}`}
                data-ga-label={scoutGaLabel}
              >
                辞退する
              </a>
            </div>
          </>
        )}
      </div>
    </>
  );
};
